const encodeText = (value) => new TextEncoder().encode(value);

const bytesToHex = (bytes) =>
    Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

const hashPathId = async (material) => {
    const digest = await crypto.subtle.digest('SHA-256', encodeText(material));
    return bytesToHex(new Uint8Array(digest)).slice(0, 40);
};

/**
 * Legacy vault path keyed only by salt (pre-account model).
 */
export const getLegacyYandexVaultPath = async (salt) => {
    if (!salt || typeof salt !== 'string') {
        throw new Error('Соль не задана');
    }
    const id = await hashPathId(`selfpass:yandex-vault:${salt}`);
    return `app:/selfpass-${id}.dat`;
};

/**
 * Deterministic vault path on Yandex Disk.
 * Prefer accountId + vaultId so sync/import stay scoped to that account
 * (including unconfirmed). Falls back to salt-only path for older installs.
 *
 * @param {string|{ accountId?: string, vaultId?: string, salt?: string }} params
 *        string = legacy salt-only path
 */
export const getYandexVaultPath = async (params) => {
    if (typeof params === 'string') {
        return getLegacyYandexVaultPath(params);
    }

    const {accountId, vaultId, salt} = params || {};

    if (accountId && vaultId) {
        const id = await hashPathId(
            `selfpass:yandex-vault:account:${accountId}:vault:${vaultId}`
        );
        return `app:/selfpass-${id}.dat`;
    }

    if (salt) {
        return getLegacyYandexVaultPath(salt);
    }

    throw new Error('Не задан account/vault или соль для пути синхронизации');
};

/**
 * Resolve remote blob: try account path first, then migrate from legacy salt path.
 */
export const resolveRemoteVault = async (api, {accountId, vaultId, salt}) => {
    const primaryPath = await getYandexVaultPath({accountId, vaultId, salt});
    const primaryContent = (await api.downloadFile(primaryPath)) || '';

    if (primaryContent) {
        return {
            path: primaryPath,
            content: primaryContent,
            fromLegacy: false,
        };
    }

    if (salt && accountId && vaultId) {
        const legacyPath = await getLegacyYandexVaultPath(salt);
        if (legacyPath !== primaryPath) {
            const legacyContent = (await api.downloadFile(legacyPath)) || '';
            if (legacyContent) {
                return {
                    path: primaryPath,
                    content: legacyContent,
                    fromLegacy: true,
                    legacyPath,
                };
            }
        }
    }

    return {
        path: primaryPath,
        content: '',
        fromLegacy: false,
    };
};

export class YandexDiskApi {
    constructor(accessToken) {
        this.accessToken = accessToken;
    }

    async request(url, method = 'GET', body = null) {
        const response = await fetch(`https://cloud-api.yandex.net/v1/disk${url}`, {
            method,
            headers: {
                Authorization: `OAuth ${this.accessToken}`,
                'Content-Type': 'application/json'
            },
            body: body ? JSON.stringify(body) : null
        });

        if (!response.ok) {
            const details = await response.text();
            throw new Error(details || `Yandex Disk HTTP ${response.status}`);
        }
        return response.json();
    }

    async uploadFile(path, content) {
        const encodedPath = encodeURIComponent(path);
        const uploadUrl = await this.request(
            `/resources/upload?path=${encodedPath}&overwrite=true`,
            'GET'
        );

        const put = await fetch(uploadUrl.href, {
            method: 'PUT',
            body: content
        });
        if (!put.ok) {
            throw new Error(`Не удалось загрузить файл на Яндекс.Диск (${put.status})`);
        }
    }

    async downloadFile(path) {
        try {
            const encodedPath = encodeURIComponent(path);
            const downloadUrl = await this.request(
                `/resources/download?path=${encodedPath}`,
                'GET'
            );

            const response = await fetch(downloadUrl.href);
            if (!response.ok) return '';
            return (await response.text()).replace(/^\uFEFF/, '').trim();
        } catch (error) {
            const msg = String(error?.message || error);
            // Нет файла на диске — нормальный случай для первого sync
            if (/DiskNotFoundError|"error"\s*:\s*"DiskNotFoundError"|HttpStatusCode"?\s*:\s*404|\b404\b/i.test(msg)) {
                return '';
            }
            throw error instanceof Error ? error : new Error(msg);
        }
    }
}
